import React, { useState, useEffect } from 'react'
import { Box, Typography, Card, CardContent, Grid, Button, CircularProgress, Paper } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import StorageIcon from '@mui/icons-material/Storage'
import MemoryIcon from '@mui/icons-material/Memory'
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined'
import { useAuth } from '../../hooks/useAuth'
import api from '../../Services/api'
import { useSnackbar } from 'notistack'

const SystemHealth = () => {
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [backupLoading, setBackupLoading] = useState(false)
  const { token } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const fetchMetrics = async () => {
    try {
      setLoading(true)
      const res = await fetch(api.apiUrl(api.API_ENDPOINTS.ADMIN.SYSTEM_METRICS), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setMetrics(data)
      } else {
        throw new Error("Erreur de récupération des métriques")
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMetrics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBackup = async () => {
    try {
      setBackupLoading(true)
      const res = await fetch(api.apiUrl(api.API_ENDPOINTS.ADMIN.SYSTEM_BACKUP), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        enqueueSnackbar(`Sauvegarde réussie : ${data.backup_file}`, { variant: 'success' })
      } else {
        throw new Error("Erreur lors de la sauvegarde")
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setBackupLoading(false)
    }
  }

  if (loading && !metrics) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>Santé du Système & Maintenance</Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={1}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <StorageIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Taille Base de Données (SQLite)</Typography>
                <Typography variant="h5">{metrics?.db_size_mb} MB</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={1}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <MemoryIcon color="secondary" sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Empreinte Mémoire (RAM API)</Typography>
                <Typography variant="h5">{metrics?.memory_mb} MB</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={1}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <MemoryOutlinedIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Utilisation CPU (API)</Typography>
                <Typography variant="h5">{metrics?.cpu_percent} %</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>Actions de Maintenance</Typography>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="body1" paragraph>
          Vous pouvez déclencher une sauvegarde manuelle de la base de données. Le fichier SQLite sera copié dans le dossier <code>/backups</code> du serveur.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={backupLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} 
          onClick={handleBackup}
          disabled={backupLoading}
        >
          {backupLoading ? "Sauvegarde en cours..." : "Créer une sauvegarde (dump)"}
        </Button>
      </Paper>
    </Box>
  )
}

export default SystemHealth
